import { LinkOutlined } from '@ant-design/icons'
import {
  Alert,
  Button,
  Modal,
  Space,
  Table,
  TableColumnsType,
  Tag,
  Typography,
  message,
} from 'antd'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'

import { axiosInstance } from '../../../../../hooks/useAxios'
import { IProduct } from '../../../../../types/product'
import { numberFormat } from '../../../../../utils'
import { ISkuLinkTarget } from '../../../../../utils/platform-link.util'
import ProductPlatformLinksModal from '../../../../Product/components/ProductPlatformLinksModal'
import {
  IStockBackfillPreview,
  IStockBackfillProduct,
  IStockBackfillSku,
} from '../types'
import SkuLinkModal from './SkuLinkModal'

interface Props {
  open: boolean
  shopId?: string
  // ชื่อร้านที่เลือกในตัวกรอง — ไม่มี = ทุกร้าน
  shopName?: string
  onClose: () => void
  onStarted: () => void
}

const STATUS_LABEL: Record<
  IStockBackfillSku['status'],
  { text: string; color: string }
> = {
  ready: { text: 'ตัดได้', color: 'green' },
  not_found: { text: 'ยังไม่ได้ผูกสินค้า', color: 'orange' },
  ambiguous: { text: 'ผูกซ้ำหลายสินค้า', color: 'red' },
}

const skuKey = (row: IStockBackfillSku) =>
  `${row.shopId ?? ''}::${row.skuId ?? ''}::${row.sku ?? ''}::${
    row.productId ?? ''
  }`

// ดูก่อนว่าจะตัดสต๊อกย้อนหลัง SKU ไหนกี่ชิ้น แล้วค่อยกดยืนยัน
// (ร้านอาจปรับสต๊อกมือชดเชยไปแล้ว — ตัดโดยไม่ดูก่อนจะกลายเป็นตัดซ้ำ)
const StockBackfillModal = ({
  open,
  shopId,
  shopName,
  onClose,
  onStarted,
}: Props) => {
  const [preview, setPreview] = useState<IStockBackfillPreview | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loadError, setLoadError] = useState<boolean>(false)
  const [isStarting, setIsStarting] = useState<boolean>(false)
  const [startError, setStartError] = useState<string | null>(null)
  // แก้การผูกจากในนี้ได้เลย — บันทึกแล้วโหลด preview ใหม่ให้เห็นผลทันที
  const [linkingProduct, setLinkingProduct] = useState<IProduct | null>(null)
  const [loadingProductId, setLoadingProductId] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState<number>(0)
  const [linkTarget, setLinkTarget] = useState<ISkuLinkTarget | null>(null)

  useEffect(() => {
    if (!open) {
      return
    }
    let cancelled = false
    setPreview(null)
    setLoadError(false)
    setStartError(null)
    setIsLoading(true)
    axiosInstance
      .get<IStockBackfillPreview>('/tiktok/backfill-stock/preview', {
        params: shopId ? { shopId } : {},
      })
      .then((res) => !cancelled && setPreview(res.data))
      .catch(() => !cancelled && setLoadError(true))
      .finally(() => !cancelled && setIsLoading(false))
    return () => {
      cancelled = true
    }
  }, [open, shopId, reloadKey])

  const openPlatformLinks = async (objectId: string) => {
    setLoadingProductId(objectId)
    try {
      const { data } = await axiosInstance.get<IProduct>(`/product/${objectId}`)
      setLinkingProduct(data)
    } catch (e) {
      message.error('โหลดข้อมูลสินค้าไม่สำเร็จ กรุณาลองใหม่')
    } finally {
      setLoadingProductId(null)
    }
  }

  const renderProduct = (product: IStockBackfillProduct) => (
    <div>
      <Typography.Link
        disabled={loadingProductId === product.objectId}
        onClick={() => openPlatformLinks(product.objectId)}
      >
        {product.name}
      </Typography.Link>
      <div style={{ fontSize: 12, color: '#8c8c8c' }}>
        {product.code}
        {product.isCollection ? ' · สินค้าแบบเซ็ต (ตัดสินค้าย่อย)' : ''}
      </div>
    </div>
  )

  const sum = (status: IStockBackfillSku['status']) =>
    (preview?.skus ?? [])
      .filter((row) => row.status === status)
      .reduce((total, row) => total + row.quantity, 0)
  const readyQuantity = sum('ready')
  const cancelledQuantity = preview?.cancelled?.quantity ?? 0
  const canStart = readyQuantity > 0 || cancelledQuantity > 0

  const handleStart = async () => {
    setIsStarting(true)
    setStartError(null)
    try {
      await axiosInstance.post(
        '/tiktok/backfill-stock',
        shopId ? { shopId } : {},
      )
      onStarted()
      onClose()
    } catch (e) {
      const err = e as AxiosError
      setStartError(
        err.response?.status === 409
          ? 'กำลังตัดสต๊อกย้อนหลังอยู่ กรุณารอให้รอบปัจจุบันเสร็จ'
          : 'เริ่มตัดสต๊อกย้อนหลังไม่สำเร็จ กรุณาลองใหม่',
      )
    } finally {
      setIsStarting(false)
    }
  }

  const columns: TableColumnsType<IStockBackfillSku> = [
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      render: (sku: string, row) => (
        <div>
          <div>{sku || '-'}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
            sku_id: {row.skuId || '-'}
          </div>
        </div>
      ),
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      render: (status: IStockBackfillSku['status']) => (
        <Tag color={STATUS_LABEL[status]?.color}>
          {STATUS_LABEL[status]?.text ?? status}
        </Tag>
      ),
    },
    {
      title: 'สินค้าในระบบ',
      key: 'product',
      render: (_, row) => {
        if (row.product) {
          return renderProduct(row.product)
        }
        if (row.status === 'ambiguous' && row.candidates?.length) {
          return (
            <Space direction="vertical" size={4}>
              {row.candidates.map((candidate) => (
                <div key={candidate.objectId}>{renderProduct(candidate)}</div>
              ))}
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                ผูก SKU นี้ไว้ {row.candidates.length} สินค้า —
                เอาออกให้เหลือสินค้าเดียว
              </Typography.Text>
            </Space>
          )
        }
        if (row.status === 'not_found') {
          return (
            <Button
              size="small"
              icon={<LinkOutlined />}
              onClick={() =>
                setLinkTarget({
                  shopId: row.shopId,
                  productId: row.productId,
                  skuId: row.skuId,
                  sku: row.sku,
                })
              }
            >
              ผูกสินค้า
            </Button>
          )
        }
        return '-'
      },
    },
    {
      title: 'ชิ้น',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      render: (value: number) => numberFormat(value, 0),
    },
  ]

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="ตัดสต๊อกย้อนหลัง"
      width={860}
      okText={
        readyQuantity > 0
          ? `ยืนยันตัดสต๊อก ${numberFormat(readyQuantity, 0)} ชิ้น`
          : 'ยืนยัน'
      }
      cancelText="ยกเลิก"
      onOk={handleStart}
      confirmLoading={isStarting}
      okButtonProps={{ disabled: isLoading || !canStart }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Text type="secondary">
          ตัดสต๊อกให้ชิ้นที่ขายไปตอนร้านยังไม่ผูก SKU โดยลงวันที่ที่ขายจริง —
          ทำกับ{shopName ? `ร้าน ${shopName}` : 'ทุกร้าน'}ทั้งหมด
          ไม่ขึ้นกับช่วงวันที่ที่เลือก
        </Typography.Text>

        <Alert
          type="warning"
          showIcon
          message="ถ้าเคยปรับสต๊อกสินค้าเหล่านี้ด้วยมือเพื่อชดเชยไปแล้ว อย่ากดยืนยัน — สต๊อกจะถูกตัดซ้ำ"
        />

        {loadError && (
          <Alert
            type="error"
            message="โหลดรายการไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"
          />
        )}
        {startError && <Alert type="error" message={startError} />}

        {preview && (
          <Space direction="vertical" size={2}>
            <Typography.Text>
              ตัดได้ <b>{numberFormat(readyQuantity, 0)}</b> ชิ้น
            </Typography.Text>
            {sum('not_found') + sum('ambiguous') > 0 && (
              <Typography.Text type="secondary">
                ยังตัดไม่ได้{' '}
                {numberFormat(sum('not_found') + sum('ambiguous'), 0)} ชิ้น — กด
                &quot;ผูกสินค้า&quot; หรือกดชื่อสินค้าที่ผูกซ้ำในตารางด้านล่าง
              </Typography.Text>
            )}
            {cancelledQuantity > 0 && (
              <Typography.Text type="secondary">
                ออเดอร์ยกเลิกไปแล้ว {numberFormat(cancelledQuantity, 0)} ชิ้น —
                ปิดรายการทิ้ง ไม่ตัด
              </Typography.Text>
            )}
            {(preview.unknown?.quantity ?? 0) > 0 && (
              <Typography.Text type="secondary">
                ไม่พบข้อมูลออเดอร์ {numberFormat(preview.unknown.quantity, 0)}{' '}
                ชิ้น — ข้ามไว้ก่อน
              </Typography.Text>
            )}
          </Space>
        )}

        <Table
          rowKey={skuKey}
          columns={columns}
          dataSource={preview?.skus ?? []}
          loading={isLoading}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          size="small"
          locale={{
            emptyText: isLoading ? ' ' : 'ไม่มีรายการที่สต๊อกยังไม่ถูกตัด',
          }}
        />
      </Space>
      <SkuLinkModal
        open={!!linkTarget}
        target={linkTarget}
        onClose={() => setLinkTarget(null)}
        onLinked={() => setReloadKey((key) => key + 1)}
      />
      {linkingProduct && (
        <ProductPlatformLinksModal
          isOpen={!!linkingProduct}
          onClose={() => setLinkingProduct(null)}
          product={linkingProduct}
          onUpdateProductSuccess={() => setReloadKey((key) => key + 1)}
        />
      )}
    </Modal>
  )
}

export default StockBackfillModal
